import { useOktaAuth } from '@okta/okta-react';
import { useSelector } from 'react-redux';
import type { RootStateOrAny } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Modules, UserRole } from '../../@types/global';
import routes from '../../routes/routes.constants';
import { PracticeFeatureFlagKey, useFeatureFlag } from '../../utils/user';

import { useJwtAuth } from './useJwtAuth';

//**
// * useAppContext
// * A custom hook that returns the current app context and user information
// *
// * @returns {object} - Returns an object with the following properties:
// * - `directorRole` - Boolean value indicating if the user is a dental director
// * - `demoContext` - Boolean value indicating if the user is in demo mode
// * - `authenticated` - Boolean value indicating if the user is authenticated
// * - `oktaAuth` - Okta auth object
// * - `user` - User object
// *
// */
export const useAppContext = () => {
  const oktaAuth = useOktaAuth() || null;
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  const location = useLocation();
  const { pathname } = location;
  const routeCheck = (route: string) => pathname.includes(route) || false;

  const appPath = () => {
    switch (true) {
      case routeCheck(routes.VIDEA_INSIGHT):
        return Modules.Insights;
      case routeCheck(routes.ANNOTATOR):
        return Modules.Viewer;
      case routeCheck(routes.INSIGHTS):
        return Modules.Insights;
      case routeCheck(routes.SWITCHBOARD):
        return Modules.Switchboard;
      case routeCheck(routes.UPLOADER):
        return Modules.Huddle;
      case routeCheck(routes.CLAIMS):
        return Modules.Claims;
      case routeCheck(routes.AMBIENT):
        return Modules.Ambient;
      case routeCheck(routes.LANDING_PAGE):
        return Modules.LandingPage;
      case routeCheck(routes.EXTERNAL):
        return Modules.External;
      case routeCheck(routes.MAGIC):
        return Modules.Magic;
      case routeCheck(routes.INSURANCE_EXPORT):
        return Modules.InsuranceExport;
      case routeCheck(routes.TRIAL):
        return Modules.Trial;
      default:
        return Modules.default;
    }
  };

  // Get JWT authentication state
  const { isAuthenticated: isJwtAuthenticated } = useJwtAuth();

  const isAuthenticated =
    oktaAuth.authState?.isAuthenticated || isJwtAuthenticated;

  const isDemoContext = !!useFeatureFlag(PracticeFeatureFlagKey.IS_SALES_DEMO)
    ?.value;

  const directorRole =
    !!user &&
    user &&
    isAuthenticated &&
    user.userRole?.toLowerCase() === UserRole.DENTAL_DIRECTOR.toLowerCase();

  return {
    directorRole,
    isDemoContext,
    isAuthenticated,
    oktaAuth,
    user,
    app: appPath(),
  };
};
